import { useCallback, useState } from 'react';
import { gameMap, serverConfigs } from '../consts';

export const useAuth = () => {
  const [authConfig, setAuthConfig] = useState({
    server: 'http://localhost:3300',
    gameId: gameMap.vikings.id,
  });

  const [authTokens, setAuthTokens] = useState({
    token: '',
    sessionId: '',
    gameUrl: '',
  });

  const [loading, setLoading] = useState({
    getToken: false,
    getAuth: false,
    simulate: false,
  });

  const [results, setResults] = useState({
    getToken: null,
    getAuth: null,
    simulate: null,
  });

  const [errors, setErrors] = useState({});

  const updateLoading = useCallback((step, value) => {
    setLoading((prev) => ({ ...prev, [step]: value }));
  }, []);

  const updateResult = useCallback((step, value) => {
    setResults((prev) => ({ ...prev, [step]: value }));
  }, []);

  const updateError = useCallback((step, error) => {
    setErrors((prev) => ({ ...prev, [step]: error }));
  }, []);

  const clearError = useCallback((step) => {
    setErrors((prev) => ({ ...prev, [step]: null }));
  }, []);

  const clearAllResults = useCallback(() => {
    setResults({ getToken: null, getAuth: null, simulate: null });
    setErrors({});
    setAuthTokens({ token: '', sessionId: '', gameUrl: '' });
  }, []);

  const getToken = useCallback(async () => {
    if (!authConfig.server || !authConfig.gameId) {
      updateError('getToken', "Сервер та Game ID обов'язкові");
      return false;
    }

    updateLoading('getToken', true);
    clearError('getToken');

    try {
      const url = `${authConfig.server}/api/external/game/start?gameId=${authConfig.gameId}&redirect=false`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      updateResult('getToken', data);

      if (data.url) {
        const urlParams = new URL(data.url).searchParams;
        const token = urlParams.get('token');

        if (token) {
          setAuthTokens((prev) => ({ ...prev, token, gameUrl: data.url }));
          return token;
        } else {
          updateError('getToken', 'Token не знайдено в URL відповіді');
          return false;
        }
      } else {
        updateError('getToken', 'URL не знайдено в відповіді');
        return false;
      }
    } catch (error) {
      updateError('getToken', error.message);
      updateResult('getToken', null);
      return false;
    } finally {
      updateLoading('getToken', false);
    }
  }, [authConfig, updateError, updateLoading, clearError, updateResult]);

  const getSessionId = useCallback(
    async (token) => {
      if (!token) {
        updateError('getAuth', 'Токен відсутній');
        return false;
      }

      updateLoading('getAuth', true);
      clearError('getAuth');

      try {
        const currentConfig = serverConfigs[authConfig.server];

        const graphqlQuery = {
          operationName: 'Auth',
          variables: {
            input: {
              token: token,
              clientId: currentConfig.clientId,
            },
          },
          query:
            'mutation Auth($input: AuthInput!) {\n  auth(input: $input) {\n    sessionId\n    __typename\n  }\n}\n',
        };

        const response = await fetch(currentConfig.graphqlUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(graphqlQuery),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        updateResult('getAuth', data);

        const sessionId = data.data?.auth?.sessionId;
        if (sessionId) {
          setAuthTokens((prev) => ({ ...prev, sessionId }));
          return sessionId;
        } else {
          updateError('getAuth', 'SessionId не знайдено в відповіді');
          return false;
        }
      } catch (error) {
        updateError('getAuth', error.message);
        updateResult('getAuth', null);
        return false;
      } finally {
        updateLoading('getAuth', false);
      }
    },
    [authConfig.server, updateError, updateLoading, clearError, updateResult],
  );

  const runFullAuth = useCallback(async () => {
    window.sessionStorage.setItem('sessionId', '');

    const token = await getToken();
    if (!token) return;

    const sessionId = await getSessionId(token);
    if (!sessionId) return;

    if (sessionId) {
      window.sessionStorage.setItem('sessionId', sessionId);
      return sessionId;
    }
  }, [getToken, getSessionId]);

  const getStepStatus = useCallback(
    (step) => {
      if (loading[step]) return 'loading';
      if (errors[step]) return 'error';
      if (results[step]) return 'success';
      return 'pending';
    },
    [loading, errors, results],
  );

  return {
    // State
    authConfig,
    setAuthConfig,
    authTokens,
    loading,
    results,
    errors,

    // Actions
    getToken,
    getSessionId,
    runFullAuth,
    clearAllResults,
    updateLoading,
    updateResult,
    updateError,
    clearError,
    getStepStatus,
  };
};
